import DOMPurify from "dompurify";

// Harden every anchor that survives sanitization:
// - `rel="noreferrer"` stops the page host/URL from leaking to external
//   destinations via the Referer header (the SSRF/referrer-leak finding).
// - `noopener` (and target="_blank") prevent reverse-tabnabbing.
// Registered once at module load — DOMPurify hooks are global.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node instanceof Element && node.tagName === "A" && node.hasAttribute("href")) {
    node.setAttribute("rel", "noopener noreferrer");
    node.setAttribute("target", "_blank");
  }
});

export const sanitizeHtml = (dirty: string): string => DOMPurify.sanitize(dirty);

// Injection-prone characters that should never reach a plain-text field or a
// generated URL: angle brackets (markup/`</`, `/>`), apostrophe and semicolon
// (SQL quote-breakout / statement terminator) and hash (URL fragment). Kept in
// sync with NO_SPECIAL_CHARS_REGEX in ./input-validation.ts so live
// sanitization and form validation agree.
const INPUT_SPECIAL_CHARS = /['<>#;]/g;

// Safe formatting tags allowed to survive in free-text messages. Covers what a
// rich-text editor (react-quill) emits for basic styling. Deliberately EXCLUDES
// `a` (the phishing-anchor vector), `img`, `script`, `iframe`, `style`, etc.
const SAFE_FORMATTING_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "blockquote",
];

// No attributes are allowed through — this blocks `href`, `src`, `style`, and
// every `on*` event handler, so the whitelisted tags can't carry a payload.
const SAFE_FORMATTING_ATTR: string[] = [];

/**
 * Strip dangerous HTML markup from a value, keeping its text content and a
 * small whitelist of safe formatting tags ({@link SAFE_FORMATTING_TAGS}).
 *
 * Unlike {@link sanitizeInput}, ordinary punctuation such as `'` and `#` is
 * preserved. Use this for free-text messages (chat, comments): basic styling
 * survives, but embedded links/scripts — e.g. an injected `<a href>` phishing
 * anchor or `<script>` — and all attributes are removed before the value is
 * persisted or relayed to other clients over the wire.
 */
export const stripMarkup = (dirty: string): string =>
  DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: SAFE_FORMATTING_TAGS,
    ALLOWED_ATTR: SAFE_FORMATTING_ATTR,
    KEEP_CONTENT: true,
  });

// Injection-prone punctuation removed from free-text messages even when it is
// not part of a tag. Angle brackets are handled by stripMarkup (dangerous tags
// are removed and stray `<`/`>` are HTML-encoded), so only the apostrophe
// (quote breakout) and hash (URL fragment) need to be stripped here.
const MESSAGE_SPECIAL_CHARS = /['#]/g;

/**
 * Sanitize a free-text message (chat/comments).
 *
 * Runs {@link stripMarkup} — so safe formatting survives and links/scripts are
 * removed — and then strips the remaining injection-prone characters `'` and
 * `#`. Net effect: all of `< > ' # /> </` are restricted, while basic
 * formatting (bold, lists, …) is preserved.
 */
export const sanitizeMessage = (dirty: string): string =>
  stripMarkup(dirty).replace(MESSAGE_SPECIAL_CHARS, "");

/**
 * Sanitize a plain-text input value.
 *
 * Removes every injection-prone character ({@link INPUT_SPECIAL_CHARS}:
 * `< > ' # ;`) outright and trims the result. Stripping the angle brackets
 * guarantees no HTML tag can form, so the value is safe to persist as-is — we
 * intentionally do NOT route this through DOMPurify, which would HTML-encode
 * stray brackets to `&lt;`/`&gt;` and leave entity artifacts in the field.
 */
const sanitizeInput = (dirty: string): string =>
  dirty.replace(INPUT_SPECIAL_CHARS, "").trim();
